<template>
  <div class="gs-mux-picker">
    <div class="gs-mux-picker__search">
      <gs-icon name="search" size="15" class="gs-mux-picker__search-icon" />
      <input
        v-model="search"
        data-field="mux-search"
        type="search"
        class="gs-mux-picker__search-input"
        maxlength="200"
        :placeholder="fallback ? 'Filter loaded videos by title or asset ID' : 'Search by title or paste a Mux asset ID'"
        aria-label="Search your Mux videos"
      />
    </div>

    <p v-if="fallback" data-fallback class="gs-mux-picker__note">
      Search is unavailable right now, so your Mux videos are listed page by page. Typing filters the loaded videos.
    </p>

    <div v-if="isIndexing" data-indexing class="gs-mux-picker__indexing" role="status">
      <gs-icon name="clock" size="15" class="gs-mux-picker__indexing-icon" />
      <span>{{ indexingText }}</span>
    </div>

    <div v-if="loading && !items.length" class="gs-mux-picker__loading">
      <p class="gs-mux-picker__note" role="status">{{ loadingText }}</p>
      <div class="gs-mux-picker__grid" aria-hidden="true">
        <div v-for="n in 4" :key="n" class="gs-mux-picker__skeleton">
          <span class="gs-mux-picker__skeleton-thumb"></span>
          <span class="gs-mux-picker__skeleton-line"></span>
          <span class="gs-mux-picker__skeleton-line is-short"></span>
        </div>
      </div>
    </div>
    <div v-else-if="error" class="gs-mux-picker__error" role="alert">
      <span>{{ error }}</span>
      <gs-button variant="outline" size="sm" data-action="retry-assets" @click="retry()">Try again</gs-button>
    </div>
    <p v-else-if="!items.length && query && isIndexing" class="gs-mux-picker__note">No matches yet — still indexing your library…</p>
    <p v-else-if="!items.length && query" class="gs-mux-picker__note">No videos match “{{ query }}”.</p>
    <div v-else-if="!items.length && !isIndexing" class="gs-mux-picker__empty">
      <span class="gs-mux-picker__empty-icon" aria-hidden="true"><gs-icon name="play" size="18" /></span>
      <p class="gs-mux-picker__note">Your Mux account has no videos yet. Use Upload to Mux instead.</p>
    </div>

    <p v-else-if="fallback && !visibleItems.length" class="gs-mux-picker__note">
      No loaded videos match “{{ search.trim() }}”.<template v-if="nextCursor"> Load more to search further.</template>
    </p>

    <ul
      v-if="visibleItems.length"
      class="gs-mux-picker__grid"
      :class="{ 'is-busy': loading }"
      :aria-busy="loading ? 'true' : 'false'"
      aria-label="Your Mux videos"
    >
      <li v-for="item in visibleItems" :key="item.asset_id">
        <button
          type="button"
          class="gs-mux-picker__item"
          :class="{ 'is-selected': item.asset_id === value }"
          :aria-pressed="item.asset_id === value ? 'true' : 'false'"
          :disabled="item.status === 'errored'"
          :data-asset="item.asset_id"
          @click="$emit('input', item)"
        >
          <span class="gs-mux-picker__thumb">
            <img
              v-if="item.thumbnail_url && !brokenThumbs[item.asset_id]"
              :src="item.thumbnail_url"
              alt=""
              loading="lazy"
              @error="$set(brokenThumbs, item.asset_id, true)"
            />
            <gs-icon v-else name="image" size="22" class="gs-mux-picker__thumb-fallback" />
            <span v-if="item.duration_seconds" class="gs-mux-picker__duration">{{ durationLabel(item) }}</span>
            <span v-if="item.asset_id === value" class="gs-mux-picker__check" aria-hidden="true">
              <gs-icon name="check" size="14" />
            </span>
          </span>
          <span class="gs-mux-picker__title" :title="item.title || item.asset_id">{{ item.title || item.asset_id }}</span>
          <span v-if="statusLabel(item)" class="gs-mux-picker__meta" :class="{ 'is-errored': item.status === 'errored' }">
            {{ statusLabel(item) }}
          </span>
          <gs-badge v-if="item.imported_video_id" class="gs-mux-picker__badge" data-imported>Already in GrooveVideo</gs-badge>
        </button>
      </li>
    </ul>

    <div v-if="nextCursor && !error && items.length" class="gs-mux-picker__more">
      <gs-button variant="outline" size="sm" data-action="load-more" :loading="loading" @click="load()">
        {{ loading ? 'Loading…' : 'Load more' }}
      </gs-button>
    </div>
  </div>
</template>

<script>
import { muxError } from '@/services/muxApi';
import { formatDuration } from '@/utils/duration';

export const SEARCH_DEBOUNCE_MS = 300;
export const INDEX_POLL_MS = 5000;

export function filterAssets(items, search) {
  const query = (search || '').trim().toLowerCase();
  if (!query) return items;
  return items.filter(
    item =>
      (item.title || '').toLowerCase().indexOf(query) !== -1 ||
      String(item.asset_id || '').toLowerCase().indexOf(query) !== -1
  );
}

// The search index (or the bridge behind it) is down, as opposed to a customer state
// such as not connected, rate limited or an invalid request.
export function isSearchOutage(error) {
  const response = error && error.response;
  if (!response) return false;
  const code = response.data && typeof response.data === 'object' ? response.data.error : null;
  return Number(response.status) >= 500 || code === 'mux_unavailable';
}

export function indexingMessage(count) {
  const n = Number(count) > 0 ? Math.floor(Number(count)) : 0;
  return `Indexing your Mux library… ${n.toLocaleString('en-US')} videos so far. Results will fill in as it goes.`;
}

/**
 * Searches the owner's whole Mux library on the server (GrooveAPI → VideoPlayer.gg's index).
 * `loadPage(q, cursor)` resolves { items, next_cursor, index: { status, indexed_count } }.
 * While VideoPlayer.gg is still indexing, the current query is re-run every 5 s until the
 * index is ready or the picker closes.
 */
export default {
  name: 'MuxAssetPicker',
  props: {
    value: { type: String, default: '' },
    loadPage: { type: Function, required: true },
    // (cursor) => the plain paged listing, used with client-side filtering if search is down.
    browsePage: { type: Function, default: null },
  },
  data() {
    return {
      items: [],
      nextCursor: null,
      loading: false,
      error: '',
      search: '',
      // The query the shown results belong to.
      query: '',
      index: null,
      // Server rows consumed so far (the offset the next page starts at).
      offset: 0,
      pagedBeyondFirst: false,
      // Search failed with an outage: browse page by page and filter locally.
      fallback: false,
      brokenThumbs: {},
    };
  },
  computed: {
    visibleItems() {
      return this.fallback ? filterAssets(this.items, this.search) : this.items;
    },
    isIndexing() {
      return !!(this.index && this.index.status === 'indexing');
    },
    loadingText() {
      const searching = !this.fallback && (this.query || this.search.trim());
      return searching ? 'Searching your Mux videos…' : 'Loading your Mux videos…';
    },
    indexingText() {
      return indexingMessage(this.index && this.index.indexed_count);
    },
  },
  watch: {
    search() {
      clearTimeout(this.debounceTimer);
      if (this.fallback) return;
      this.debounceTimer = setTimeout(() => this.load(true), SEARCH_DEBOUNCE_MS);
    },
  },
  created() {
    // Not reactive: timers and the latest request number.
    this.seq = 0;
    this.debounceTimer = null;
    this.pollTimer = null;
    this.closed = false;
  },
  mounted() {
    this.load(true);
  },
  beforeDestroy() {
    this.closed = true;
    clearTimeout(this.debounceTimer);
    this.stopPolling();
  },
  methods: {
    /**
     * reset: first page of the current search box text (a new search).
     * poll: silent re-run of the shown query while indexing.
     * neither: "Load more" (next page of the shown query).
     */
    async load(reset = false, { poll = false } = {}) {
      if (this.closed || (!reset && !poll && this.loading)) return;
      const request = this.beginRequest(reset, poll);
      try {
        const page = (await this.fetchPage(request)) || {};
        if (this.isCurrent(request)) this.applyPage(page, request);
      } catch (e) {
        if (this.isCurrent(request)) this.handleFailure(e, request);
      } finally {
        if (request.seq === this.seq && !poll) this.loading = false;
      }
    },
    fetchPage(request) {
      return request.fallback ? this.browsePage(request.cursor) : this.loadPage(request.query, request.cursor);
    },
    handleFailure(e, request) {
      if (!request.fallback && !request.poll && this.browsePage && isSearchOutage(e)) {
        this.startFallback();
      } else {
        this.showFailure(e, request.poll);
      }
    },
    // Import keeps working if the search index is broken: plain paged listing from the start.
    startFallback() {
      this.fallback = true;
      this.index = null;
      this.query = '';
      this.items = [];
      this.nextCursor = null;
      this.load(true);
    },
    beginRequest(reset, poll) {
      this.seq += 1;
      this.stopPolling();
      if (!poll) {
        if (reset) clearTimeout(this.debounceTimer);
        this.loading = true;
        this.error = '';
      }
      const fresh = reset && !poll;
      return {
        seq: this.seq,
        fallback: this.fallback,
        reset,
        poll,
        query: fresh && !this.fallback ? this.search.trim() : this.query,
        cursor: reset || poll ? null : this.nextCursor,
      };
    },
    // Only the newest request may change what is shown; a closed picker changes nothing.
    isCurrent(request) {
      return request.seq === this.seq && !this.closed;
    },
    showFailure(e, poll) {
      const info = muxError(e, "We couldn't load your Mux videos. Try again.");
      if (info.needsConnect) this.$emit('needs-connect');
      // A failed background re-query keeps what is shown and stops; typing searches again.
      if (!poll) this.error = info.message;
    },
    applyPage(page, request) {
      const previousCount = this.indexedCount();
      this.index = page.index && typeof page.index === 'object' ? page.index : null;
      if (request.poll && this.pagedBeyondFirst) {
        this.offerMoreIfGrown(previousCount);
      } else if (request.reset || request.poll) {
        this.replaceItems(page, request.query);
      } else {
        this.appendItems(page, request.cursor);
      }
      if (this.isIndexing) this.pollTimer = setTimeout(() => this.load(true, { poll: true }), INDEX_POLL_MS);
    },
    indexedCount() {
      return (this.index && Number(this.index.indexed_count)) || 0;
    },
    // Keep the pages the user loaded. The index fills oldest-last, so once it has grown,
    // offer "Load more" from where the user stopped.
    offerMoreIfGrown(previousCount) {
      if (!this.nextCursor && this.indexedCount() > previousCount) this.nextCursor = String(this.offset);
    },
    replaceItems(page, query) {
      const items = Array.isArray(page.items) ? page.items : [];
      this.items = items;
      this.query = query;
      this.offset = items.length;
      this.pagedBeyondFirst = false;
      this.nextCursor = page.next_cursor || null;
    },
    appendItems(page, cursor) {
      const items = Array.isArray(page.items) ? page.items : [];
      // A page boundary can shift while the library changes; never list an asset twice.
      const seen = {};
      this.items.forEach(item => { seen[item.asset_id] = true; });
      this.items = this.items.concat(items.filter(item => !seen[item.asset_id]));
      this.offset = (Number(cursor) || 0) + items.length;
      this.pagedBeyondFirst = true;
      this.nextCursor = page.next_cursor || null;
    },
    stopPolling() {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    },
    // A failed first page reloads from the start; a failed "Load more" retries that page and keeps what is shown.
    retry() {
      this.load(!this.items.length);
    },
    durationLabel(item) {
      return formatDuration(Math.round(item.duration_seconds));
    },
    statusLabel(item) {
      if (item.status === 'preparing') return 'Still preparing';
      if (item.status === 'errored') return 'Mux could not process this video';
      return '';
    },
  },
};
</script>

<style scoped>
.gs-mux-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gs-mux-picker__search {
  display: flex;
  gap: 8px;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-mux-picker__search:focus-within {
  border-color: var(--gs-active);
  box-shadow: var(--gs-active-shadow);
}

.gs-mux-picker__search-icon {
  flex: none;
  color: var(--gs-muted-foreground);
}

.gs-mux-picker__search-input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 0;
  color: var(--gs-foreground);
  background: transparent;
  border: 0;
  font-size: 14px;
}

.gs-mux-picker__search-input:focus {
  outline: none;
}

.gs-mux-picker__note {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-mux-picker__indexing {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  color: var(--gs-foreground);
  background: var(--gs-active-soft);
  border: 1px solid var(--gs-active-border);
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  line-height: 1.5;
}

.gs-mux-picker__indexing-icon {
  flex: none;
  margin-top: 2px;
  color: var(--gs-active);
}

.gs-mux-picker__grid.is-busy {
  opacity: 0.7;
  transition: opacity 150ms ease;
}

.gs-mux-picker__loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gs-mux-picker__error {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  color: var(--gs-danger);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-danger-border);
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  line-height: 1.5;
}

.gs-mux-picker__empty {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  padding: 28px 16px;
  text-align: center;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-mux-picker__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--gs-muted-foreground);
  background: var(--gs-muted);
  border-radius: 999px;
}

.gs-mux-picker__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.gs-mux-picker__item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 8px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.gs-mux-picker__item:hover:not(:disabled) {
  border-color: var(--gs-border-strong);
  box-shadow: var(--gs-shadow-sm);
}

.gs-mux-picker__item.is-selected {
  border-color: var(--gs-active);
  box-shadow: var(--gs-active-shadow);
}

.gs-mux-picker__item:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.gs-mux-picker__item:focus-visible {
  outline: 2px solid var(--gs-active);
  outline-offset: 2px;
}

.gs-mux-picker__thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  color: var(--gs-muted-foreground);
  background: var(--gs-muted);
  border-radius: var(--gs-radius-sm);
}

.gs-mux-picker__thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gs-mux-picker__duration {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 2px 6px;
  color: #fff;
  background: rgba(15, 23, 42, 0.78);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.gs-mux-picker__check {
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #fff;
  background: var(--gs-active);
  border-radius: 999px;
  box-shadow: var(--gs-shadow-sm);
}

.gs-mux-picker__title {
  max-width: 100%;
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-mux-picker__meta {
  color: var(--gs-muted-foreground);
  font-size: 12px;
}

.gs-mux-picker__meta.is-errored {
  color: var(--gs-danger);
}

.gs-mux-picker__badge {
  min-height: 20px;
  padding: 2px 8px;
  font-size: 11px;
}

.gs-mux-picker__more {
  display: flex;
  justify-content: center;
}

.gs-mux-picker__skeleton {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-mux-picker__skeleton > span {
  display: block;
  background: var(--gs-muted);
  border-radius: var(--gs-radius-sm);
  animation: gs-mux-picker-pulse 1.4s ease-in-out infinite;
}

.gs-mux-picker__skeleton-thumb {
  aspect-ratio: 16 / 9;
}

.gs-mux-picker__skeleton-line {
  width: 75%;
  height: 12px;
}

.gs-mux-picker__skeleton-line.is-short {
  width: 45%;
}

@keyframes gs-mux-picker-pulse {
  50% {
    opacity: 0.5;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gs-mux-picker__skeleton > span {
    animation: none;
  }
}

@media (max-width: 480px) {
  .gs-mux-picker__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
}
</style>
